import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, X, ShieldCheck, RefreshCw, Search } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { AccessDenied } from "@/components/AccessDenied";
import {
  assignSchemataRole,
  listSchemataRoleAudit,
  listSchemataRoleUsers,
  revokeSchemataRole,
  type SchemataRole,
} from "@/lib/admin-schemata-roles.functions";

export const Route = createFileRoute("/admin/schemata-role")({
  component: SchemataRolesPage,
  head: () => ({
    meta: [
      { title: "Správa rolí Schémata | Admin" },
      {
        name: "description",
        content:
          "Přidělování a odebírání rolí schemata_proposer, schemata_approver a schemata_implementer s auditem změn.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const ROLE_LABEL: Record<SchemataRole, string> = {
  schemata_proposer: "Navrhovatel",
  schemata_approver: "Approver",
  schemata_implementer: "Implementátor",
};

const ROLE_COLOR: Record<SchemataRole, string> = {
  schemata_proposer: "bg-blue-100 text-blue-800 border-blue-200",
  schemata_approver: "bg-amber-100 text-amber-800 border-amber-200",
  schemata_implementer: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const ALL_ROLES: SchemataRole[] = [
  "schemata_proposer",
  "schemata_approver",
  "schemata_implementer",
];

function SchemataRolesPage() {
  const { user, roles: myRoles, loading } = useAuth();
  const isAdmin = myRoles?.includes("admin");

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Načítám…</div>;
  }
  if (!user) {
    return (
      <AccessDenied
        title="Přihlaste se"
        description="Tato stránka je dostupná pouze pro administrátory."
      />
    );
  }
  if (!isAdmin) {
    return (
      <AccessDenied
        title="Přístup odepřen"
        description="Správa rolí Schémata je vyhrazená pouze administrátorům."
        requiredRole="admin"
      />
    );
  }

  return <SchemataRolesAdminContent />;
}

function SchemataRolesAdminContent() {
  const qc = useQueryClient();
  const fetchUsers = useServerFn(listSchemataRoleUsers);
  const fetchAudit = useServerFn(listSchemataRoleAudit);
  const assignFn = useServerFn(assignSchemataRole);
  const revokeFn = useServerFn(revokeSchemataRole);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | SchemataRole | "none">("all");

  const usersQ = useQuery({
    queryKey: ["admin-schemata-roles", "users"],
    queryFn: () => fetchUsers(),
  });
  const auditQ = useQuery({
    queryKey: ["admin-schemata-roles", "audit"],
    queryFn: () => fetchAudit(),
  });

  const assignMut = useMutation({
    mutationFn: (v: { userId: string; role: SchemataRole }) =>
      assignFn({ data: v }),
    onSuccess: (res, v) => {
      toast.success(
        res.changed
          ? `Přiděleno: ${ROLE_LABEL[v.role]}`
          : "Uživatel roli již měl.",
      );
      qc.invalidateQueries({ queryKey: ["admin-schemata-roles"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revokeMut = useMutation({
    mutationFn: (v: { userId: string; role: SchemataRole }) =>
      revokeFn({ data: v }),
    onSuccess: (res, v) => {
      toast.success(
        res.changed ? `Odebráno: ${ROLE_LABEL[v.role]}` : "Uživatel roli neměl.",
      );
      qc.invalidateQueries({ queryKey: ["admin-schemata-roles"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    const list = usersQ.data ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((u) => {
      if (filter === "none" && u.roles.length > 0) return false;
      if (filter !== "all" && filter !== "none" && !u.roles.includes(filter))
        return false;
      if (!q) return true;
      return (
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [usersQ.data, search, filter]);

  const counts = useMemo(() => {
    const c: Record<SchemataRole, number> = {
      schemata_proposer: 0,
      schemata_approver: 0,
      schemata_implementer: 0,
    };
    for (const u of usersQ.data ?? []) for (const r of u.roles) c[r]++;
    return c;
  }, [usersQ.data]);

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6 space-y-6">
      <header className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Správa rolí Schémata
          </h1>
          <p className="text-sm text-muted-foreground">
            Přidělujte role workflow (navrhovatel, approver, implementátor).
            Každá změna je auditována.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            qc.invalidateQueries({ queryKey: ["admin-schemata-roles"] })
          }
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Obnovit
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {ALL_ROLES.map((r) => (
          <Card key={r}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {ROLE_LABEL[r]}
                </div>
                <div className="text-2xl font-semibold">{counts[r]}</div>
              </div>
              <Badge variant="outline" className={ROLE_COLOR[r]}>
                {r}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Uživatelé</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Hledat e-mail nebo jméno…"
                className="pl-8"
              />
            </div>
            <Select
              value={filter}
              onValueChange={(v) => setFilter(v as typeof filter)}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Všichni uživatelé</SelectItem>
                <SelectItem value="none">Bez schemata rolí</SelectItem>
                {ALL_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    Má roli: {ROLE_LABEL[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Uživatel</TableHead>
                  {ALL_ROLES.map((r) => (
                    <TableHead key={r} className="text-center">
                      {ROLE_LABEL[r]}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersQ.isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                      Načítám uživatele…
                    </TableCell>
                  </TableRow>
                )}
                {!usersQ.isLoading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                      Žádní uživatelé odpovídající filtru.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.slice(0, 300).map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="font-medium">{u.full_name ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {u.email ?? u.id}
                      </div>
                    </TableCell>
                    {ALL_ROLES.map((r) => {
                      const has = u.roles.includes(r);
                      const pending =
                        (assignMut.isPending &&
                          assignMut.variables?.userId === u.id &&
                          assignMut.variables?.role === r) ||
                        (revokeMut.isPending &&
                          revokeMut.variables?.userId === u.id &&
                          revokeMut.variables?.role === r);
                      return (
                        <TableCell key={r} className="text-center">
                          <Button
                            size="sm"
                            variant={has ? "default" : "outline"}
                            disabled={pending}
                            onClick={() =>
                              has
                                ? revokeMut.mutate({ userId: u.id, role: r })
                                : assignMut.mutate({ userId: u.id, role: r })
                            }
                            className="min-w-[92px]"
                            aria-label={
                              has ? `Odebrat ${ROLE_LABEL[r]}` : `Přidělit ${ROLE_LABEL[r]}`
                            }
                          >
                            {has ? (
                              <>
                                <Check className="h-4 w-4 mr-1" /> Má
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4 mr-1" /> Nemá
                              </>
                            )}
                          </Button>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filtered.length > 300 && (
            <div className="text-xs text-muted-foreground">
              Zobrazeno prvních 300 z {filtered.length}. Zúžte filtr pro více
              detailů.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auditní historie změn rolí</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kdy</TableHead>
                  <TableHead>Akce</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Cíl</TableHead>
                  <TableHead>Admin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditQ.isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                      Načítám audit…
                    </TableCell>
                  </TableRow>
                )}
                {!auditQ.isLoading && (auditQ.data ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                      Zatím žádné záznamy.
                    </TableCell>
                  </TableRow>
                )}
                {(auditQ.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString("cs-CZ")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          row.action === "schemata_role_assign"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                            : "bg-rose-100 text-rose-800 border-rose-200"
                        }
                      >
                        {row.action === "schemata_role_assign"
                          ? "Přiděleno"
                          : "Odebráno"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {row.role ? (
                        <Badge variant="outline" className={ROLE_COLOR[row.role]}>
                          {ROLE_LABEL[row.role]}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.target_email ?? row.target_user_id ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.admin_email ?? row.admin_user_id ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
